import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Card, Btn, Tag } from '../ui/primitives';
import { fetchIncome } from '../services/connectApi';

const CardTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 750;
  color: ${({ theme }) => theme.text};
`;

const CardText = styled.p`
  margin: 0 0 14px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const Stat = styled.div`
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface2};

  .label {
    font-size: ${({ theme }) => theme.fsMicro};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.textSubtle};
    margin-bottom: 4px;
  }
  .value {
    font-size: 20px;
    font-weight: 760;
    color: ${({ theme }) => theme.text};
  }
  .value--net { color: ${({ theme }) => theme.success}; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fsSm};
  th, td { text-align: left; padding: 10px 12px; border-bottom: 1px solid ${({ theme }) => theme.border}; }
  th { color: ${({ theme }) => theme.textSubtle}; font-weight: 700; font-size: ${({ theme }) => theme.fsMicro}; text-transform: uppercase; letter-spacing: .05em; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
`;

const SubHead = styled.div`
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  margin: 18px 0 8px;
`;

function fmtDate(epochSeconds) {
  if (!epochSeconds) return '—';
  return new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function fmtMoney(cents, currency) {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch (_) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

const TYPE_LABEL = {
  charge: 'Sale', payment: 'Sale', payout: 'Payout', transfer: 'Transfer',
  refund: 'Refund', payment_refund: 'Refund', stripe_fee: 'Fee', adjustment: 'Adjustment',
};

/**
 * Billing → Income. Instructor earnings from their connected Stripe account:
 * available/pending balance, payout history, and balance activity.
 */
export default function BillingIncome() {
  const userId = localStorage.getItem('userId');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      setData(await fetchIncome(userId));
    } catch (e) {
      setError('Could not load your income right now.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const currency = data?.currency || 'usd';
  const payouts = data?.payouts || [];
  const activity = data?.transactions || [];

  return (
    <Card $pad style={{ marginBottom: 16 }}>
      <CardTitle>Income</CardTitle>
      <CardText>
        Your share of course sales, settled to your connected Stripe account and paid to your bank
        on a rolling schedule.
      </CardText>

      {loading ? (
        <CardText style={{ margin: 0, opacity: 0.6 }}>Loading your income…</CardText>
      ) : !data?.connected ? (
        <div><Tag $tone="amber">Connect a Stripe account above to start receiving payouts</Tag></div>
      ) : (
        <>
          {!data.payoutsEnabled && (
            <div style={{ marginBottom: 12 }}>
              <Tag $tone="amber">Finish Stripe onboarding to enable payouts</Tag>
            </div>
          )}

          <StatRow>
            <Stat>
              <div className="label">Available</div>
              <div className="value value--net">{fmtMoney(data.availableCents, currency)}</div>
            </Stat>
            <Stat>
              <div className="label">Pending</div>
              <div className="value">{fmtMoney(data.pendingCents, currency)}</div>
            </Stat>
          </StatRow>

          <SubHead>Recent activity</SubHead>
          <Table>
            <thead>
              <tr><th>Date</th><th>Type</th><th className="num">Gross</th><th className="num">Fee</th><th className="num">Net</th></tr>
            </thead>
            <tbody>
              {activity.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '18px 0', opacity: 0.6 }}>No activity yet — earnings appear here after your first sale.</td></tr>
              ) : activity.map((t) => (
                <tr key={t.id}>
                  <td>{fmtDate(t.created)}</td>
                  <td>{TYPE_LABEL[t.type] || t.type}</td>
                  <td className="num">{fmtMoney(t.grossCents, t.currency || currency)}</td>
                  <td className="num">{fmtMoney(t.feeCents, t.currency || currency)}</td>
                  <td className="num">{fmtMoney(t.netCents, t.currency || currency)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <SubHead>Payouts to your bank</SubHead>
          <Table>
            <thead>
              <tr><th>Initiated</th><th>Arrives</th><th className="num">Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '18px 0', opacity: 0.6 }}>No payouts yet.</td></tr>
              ) : payouts.map((p) => (
                <tr key={p.id}>
                  <td>{fmtDate(p.created)}</td>
                  <td>{fmtDate(p.arrivalDate)}</td>
                  <td className="num">{fmtMoney(p.amountCents, p.currency || currency)}</td>
                  <td style={{ textTransform: 'capitalize' }}>{(p.status || '').replace('_', ' ')}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {error && <CardText style={{ margin: '12px 0 0', color: 'oklch(0.55 0.16 25)' }}>{error}</CardText>}
    </Card>
  );
}
